import { Check, Lock } from "lucide-react";
import { Button } from "@finora/ui";
import type { SelectedPackage } from "./PackageStep";

interface Props {
  selectedPackage: SelectedPackage | null;
  onNext: () => void;
  onBack: () => void;
}

/**
 * Onboarding, korak "Moduli" - NAMERNO read-only pregled modula uključenih
 * u paket izabran u prethodnom koraku (EntitlementService pristup modulu
 * određuje IZ PAKETA, ne iz ručnih toggle-a po modulu - vidi razgovor).
 * Nema self-service "uključi/isključi modul" endpoint-a za tenant, samo
 * super-admin CRUD - zato ovaj korak ne šalje nikakav zahtev, samo
 * informiše i prelazi dalje. Dodatni moduli van paketa su prikazani kao
 * zaključani, sa uputstvom da nadogradnja paketa ide kroz "Moj paket".
 */
export function ModulesStep({ selectedPackage, onNext, onBack }: Props) {
  const included = selectedPackage?.includedModules ?? [];

  return (
    <div className="rounded-xl border border-border bg-surface p-8 shadow-subtle">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand">Dostupni moduli</p>
          <h1 className="mb-1 text-2xl font-bold text-slate-900">
            Moduli uključeni u paket {selectedPackage?.name ?? ""}
          </h1>
          <p className="text-sm text-slate-500">
            Ovi moduli su odmah dostupni. Dodatne module možete uključiti kasnije nadogradnjom paketa (Moj paket).
          </p>
        </div>
      </div>

      {included.length === 0 ? (
        <p className="text-sm text-slate-400">Nijedan modul nije uključen u izabrani paket.</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {included.map((mod) => (
            <div
              key={mod.id}
              className="flex items-center justify-between rounded-lg border border-border bg-app-bg px-4 py-3"
            >
              <div>
                <p className="text-sm font-medium text-slate-900">{mod.name}</p>
                {mod.description && <p className="text-xs text-slate-500">{mod.description}</p>}
              </div>
              <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                <Check className="h-3 w-3" /> Uključeno
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 rounded-lg bg-app-bg p-3 text-xs text-slate-500">
        <Lock className="h-3.5 w-3.5 flex-shrink-0" />
        Potreban vam je dodatni modul koji nije uključen u vaš paket? Možete ga dodati kasnije u okviru "Moj paket".
      </div>

      <div className="mt-6 flex justify-between border-t border-border pt-6">
        <Button variant="secondary" onClick={onBack}>
          ← Nazad
        </Button>
        <Button onClick={onNext}>Nastavi →</Button>
      </div>
    </div>
  );
}
